import React from 'react';
import axios from 'axios';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  ControlBar,
  useTracks,
  useParticipants,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #982 — cross-component signal for "who is currently speaking in voice".
// Emits Set<identity> whenever LiveKit's active-speaker list changes so the
// userlist can render a ⚡ pulse next to those users without wiring props
// through the entire ChatPage tree.
const speakingListeners = new Set();
let latestSpeakers = new Set();
export const subscribeSpeaking = (fn) => {
  speakingListeners.add(fn);
  try { fn(latestSpeakers); } catch { /* ignore */ }
  return () => { speakingListeners.delete(fn); };
};
const publishSpeaking = (set) => {
  latestSpeakers = new Set(set);
  for (const fn of speakingListeners) {
    try { fn(latestSpeakers); } catch { /* ignore */ }
  }
};

// Runs INSIDE <LiveKitRoom>. Watches every participant's isSpeaking flag
// and republishes the identity set to the global emitter (1x per animation frame).
const SpeakingWatcher = () => {
  const participants = useParticipants();
  React.useEffect(() => {
    const compute = () => {
      const active = new Set();
      for (const p of participants) {
        if (p && p.isSpeaking && p.identity) active.add(p.identity);
      }
      publishSpeaking(active);
    };
    compute();
    const id = setInterval(compute, 300); // participants array is stable; poll for isSpeaking flips
    return () => clearInterval(id);
  }, [participants]);
  return null;
};

/**
 * Msg #981 — Voice / Video / Screenshare panel powered by LiveKit Cloud.
 *
 * The parent (`ChatPage`) opens this panel when the user clicks the 🎙 Join
 * Voice pill. On mount we POST /api/rtc/token to mint a short-lived JWT for
 * (room, user_id, user_name), then hand it to <LiveKitRoom>. When LiveKit is
 * not configured on the backend (stub keys), the panel renders a friendly
 * "not configured" state instead of crashing.
 */
const RoomTiles = () => {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  return (
    <GridLayout tracks={tracks} style={{ height: '100%', width: '100%' }}>
      <ParticipantTile />
    </GridLayout>
  );
};

export const VoiceVideoPanel = ({ room, userId, userName, onLeave, autoMic = true, mode = 'voice_video' }) => {
  const [token, setToken] = React.useState('');
  const [serverUrl, setServerUrl] = React.useState('');
  const [error, setError] = React.useState('');
  const [ready, setReady] = React.useState(null); // null=loading, true/false=known
  const initialVideo = mode === 'voice_video';
  const [audioOn, setAudioOn] = React.useState(!!autoMic);
  const [videoOn, setVideoOn] = React.useState(initialVideo);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await axios.get(`${API}/rtc/config`);
        if (cancelled) return;
        if (!cfg.data.ready) {
          setReady(false);
          setError(
            'Voice / Video is not configured yet. The Administrator needs to drop the LiveKit keys into the server, then this pill will just work.',
          );
          return;
        }
        setReady(true);
        setServerUrl(cfg.data.url);
        const r = await axios.post(`${API}/rtc/token`, {
          room, user_id: userId, user_name: userName || userId,
        });
        if (!cancelled) setToken(r.data.token);
      } catch (e) {
        if (cancelled) return;
        const detail = e && e.response && e.response.data && e.response.data.detail;
        setReady(false);
        setError(detail || 'Could not connect to voice/video.');
      }
    })();
    return () => { cancelled = true; publishSpeaking(new Set()); };
  }, [room, userId, userName]);

  const panelStyle = {
    position: 'fixed',
    // Msg #984 — Nudged left+up 4px to prevent overlap with the ChatPage SEND
    // button at ~1600×900 viewport (testing-agent flag). At smaller viewports
    // we also cap width/height so mobile Chrome doesn't crop the ControlBar.
    right: 16, bottom: 88,
    width: 'min(480px, calc(100vw - 32px))',
    height: 'min(380px, calc(100vh - 140px))',
    background: '#0f0f10',
    border: '2px solid #2d6b1e',
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    display: 'flex', flexDirection: 'column',
    zIndex: 9999,
    overflow: 'hidden',
  };
  const headerStyle = {
    padding: '4px 10px',
    background: '#2d6b1e',
    color: '#f5f5e8',
    fontSize: 12, fontWeight: 'bold', letterSpacing: 1,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  };

  if (ready === false) {
    return (
      <div data-testid="voice-video-panel" style={panelStyle}>
        <div style={headerStyle}>
          <span>🎙 VOICE — {room}</span>
          <span
            data-testid="voice-video-close"
            onClick={onLeave}
            style={{ cursor: 'pointer', padding: '0 6px' }}
          >✕</span>
        </div>
        <div style={{
          padding: 24, color: '#e8e8dc', fontSize: 13, lineHeight: 1.5,
          textAlign: 'center', flex: 1,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🎙</div>
          <div>{error || 'Voice/Video not available.'}</div>
        </div>
      </div>
    );
  }

  if (!token || !serverUrl) {
    return (
      <div data-testid="voice-video-panel" style={panelStyle}>
        <div style={headerStyle}>
          <span>🎙 VOICE — {room}</span>
          <span
            data-testid="voice-video-close"
            onClick={onLeave}
            style={{ cursor: 'pointer', padding: '0 6px' }}
          >✕</span>
        </div>
        <div style={{ padding: 20, color: '#e8e8dc', fontSize: 13, textAlign: 'center', flex: 1 }}>
          Connecting to voice…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="voice-video-panel" style={panelStyle}>
      <div style={headerStyle}>
        <span>🎙 VOICE / VIDEO — {room}</span>
        <span
          data-testid="voice-video-close"
          onClick={onLeave}
          style={{ cursor: 'pointer', padding: '0 6px' }}
          title="Leave voice/video"
        >✕</span>
      </div>
      <LiveKitRoom
        video={videoOn}
        audio={audioOn}
        token={token}
        serverUrl={serverUrl}
        data-lk-theme="default"
        onDisconnected={onLeave}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <SpeakingWatcher />
        <div style={{ flex: 1, minHeight: 0, background: '#111' }}>
          <RoomTiles />
        </div>
        <div style={{
          padding: '4px 8px',
          background: '#1a1a1c',
          borderTop: '1px solid #2d6b1e',
          display: 'flex', justifyContent: 'center',
        }}>
          <ControlBar variation="minimal" controls={{
            microphone: true, camera: true, screenShare: true,
            chat: false, settings: false, leave: true,
          }} />
        </div>
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
};

export default VoiceVideoPanel;
